import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nContext";
import {
  Stethoscope, Plus, Trash2, Calendar, Building2,
  CheckCircle2, Circle, ClipboardList, X, ChevronDown, ChevronUp,
} from "lucide-react";

const DEFAULT_VISIT_TYPES = [
  "Initial consultation", "Follow-up visit", "Physical therapy",
  "Chiropractic adjustment", "Imaging - X-ray", "Imaging - MRI",
  "Imaging - CT scan", "Specialist consultation", "Pain management injection",
  "Surgery", "ER / Urgent care", "Other",
];

function StatTile({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E0E4E2] bg-white p-3">
      <div className="label-cap text-[10px]">{label}</div>
      <div className="mt-1 font-display text-xl font-black tracking-tight text-[#11322A]">{value}</div>
    </div>
  );
}

function blankForm() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    physician_name: "",
    physician_specialty: "",
    facility: "",
    visit_date: today,
    visit_type: "Follow-up visit",
    notes: "",
    completed: true,
  };
}

export default function CaseTreatments({ caseId, scope = "claimant" }) {
  const t = useT();
  const listUrl = scope === "admin" ? `/admin/leads/${caseId}/treatments` : `/me/cases/${caseId}/treatments`;
  const createUrl = scope === "admin" ? `/admin/leads/${caseId}/treatments` : `/me/cases/${caseId}/treatments`;
  const itemUrl = (id) => `/me/cases/${caseId}/treatments/${id}`; // PATCH/DELETE always through claimant route (admin allowed by role check)

  const [items, setItems] = useState([]);
  const [visitTypes, setVisitTypes] = useState(DEFAULT_VISIT_TYPES);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const [tx, vt] = await Promise.all([
        api.get(listUrl),
        api.get("/treatments/visit-types"),
      ]);
      setItems(tx.data);
      setVisitTypes(vt.data?.visit_types || DEFAULT_VISIT_TYPES);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (caseId) load(); }, [caseId]);

  const add = async (e) => {
    e?.preventDefault();
    if (!form.physician_name.trim()) return toast.error(t("tx.required"));
    setAdding(true);
    try {
      await api.post(createUrl, form);
      toast.success(t("tx.toast.saved"));
      setForm(blankForm());
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (t) => {
    try {
      await api.patch(itemUrl(t.id), { completed: !t.completed });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (tx) => {
    if (!confirm(t("tx.delete_confirm", { physician: tx.physician_name, date: tx.visit_date }))) return;
    try {
      await api.delete(itemUrl(tx.id));
      toast.success(t("tx.toast.removed"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const totals = (() => {
    const total = items.length;
    const completed = items.filter((i) => i.completed).length;
    const physicians = new Set(items.map((i) => (i.physician_name || "").trim().toLowerCase()).filter(Boolean));
    return { total, completed, physicians: physicians.size };
  })();

  // Group by physician
  const grouped = (() => {
    const map = new Map();
    for (const i of items) {
      const key = (i.physician_name || "Unknown").trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  })();

  return (
    <div data-testid={`treatments-${scope}`}>
      <div className="grid grid-cols-3 gap-3">
        <StatTile label={t("tx.total")} value={totals.total} />
        <StatTile label={t("tx.completed")} value={totals.completed} />
        <StatTile label={t("tx.physicians")} value={totals.physicians} />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#5C6B68]">
          <ClipboardList className="h-3.5 w-3.5" /> {t("tx.record")}
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="btn-primary !py-2 text-sm"
          data-testid="tx-add-toggle"
        >
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? t("tx.cancel") : t("tx.add")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={add} className="mt-3 rounded-xl border border-[#E0E4E2] bg-[#FDFBF7] p-4" data-testid="tx-add-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label-cap text-[10px]">{t("tx.physician")}</label>
              <input className="input-field mt-1" value={form.physician_name} onChange={(e) => set("physician_name", e.target.value)} placeholder={t("tx.physician_placeholder")} required data-testid="tx-physician" />
            </div>
            <div>
              <label className="label-cap text-[10px]">{t("tx.specialty")}</label>
              <input className="input-field mt-1" value={form.physician_specialty} onChange={(e) => set("physician_specialty", e.target.value)} placeholder={t("tx.specialty_placeholder")} data-testid="tx-specialty" />
            </div>
            <div>
              <label className="label-cap text-[10px]">{t("tx.facility")}</label>
              <input className="input-field mt-1" value={form.facility} onChange={(e) => set("facility", e.target.value)} placeholder={t("tx.facility_placeholder")} data-testid="tx-facility" />
            </div>
            <div>
              <label className="label-cap text-[10px]">{t("tx.visit_date")}</label>
              <input type="date" className="input-field mt-1" value={form.visit_date} onChange={(e) => set("visit_date", e.target.value)} required data-testid="tx-date" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-cap text-[10px]">{t("tx.visit_type")}</label>
              <select className="input-field mt-1" value={form.visit_type} onChange={(e) => set("visit_type", e.target.value)} data-testid="tx-type">
                {visitTypes.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label-cap text-[10px]">{t("tx.notes")}</label>
              <textarea rows={2} className="input-field mt-1" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("tx.notes_placeholder")} data-testid="tx-notes" />
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm font-semibold text-[#0B1317]">
              <input type="checkbox" checked={form.completed} onChange={(e) => set("completed", e.target.checked)} data-testid="tx-completed" />
              {t("tx.occurred")}
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary !py-2 text-sm">{t("cancel")}</button>
            <button type="submit" disabled={adding} className="btn-primary !py-2 text-sm disabled:opacity-50" data-testid="tx-save">
              {adding ? t("tx.saving") : t("tx.save")}
            </button>
          </div>
        </form>
      )}

      <div className="mt-4">
        {loading && <div className="py-6 text-center text-sm text-[#5C6B68]">{t("tx.loading")}</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-lg border border-[#E0E4E2] bg-white p-6 text-center text-sm text-[#5C6B68]" data-testid="tx-empty">
            {t("tx.empty", { add_visit: t("tx.empty.add_visit") })}
          </div>
        )}
        {!loading && grouped.length > 0 && (
          <ul className="space-y-4" data-testid="tx-list">
            {grouped.map(([physician, visits]) => (
              <li key={physician} className="overflow-hidden rounded-xl border border-[#E0E4E2] bg-white" data-testid={`tx-physician-${physician}`}>
                <div className="flex items-center justify-between border-b border-[#E0E4E2] bg-[#FDFBF7] px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-[#11322A]" />
                    <div className="font-display text-sm font-bold text-[#0B1317]">{physician}</div>
                    {visits[0].physician_specialty && (
                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#5C6B68] border border-[#E0E4E2]">
                        {visits[0].physician_specialty}
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-bold text-[#5C6B68]">
                    {t(visits.length === 1 ? "tx.visit_count_one" : "tx.visit_count_other", { n: visits.length })}
                  </div>
                </div>
                <ul className="divide-y divide-[#E0E4E2]">
                  {visits.map((v) => (
                    <li key={v.id} className="flex items-start gap-3 px-4 py-3" data-testid={`tx-row-${v.id}`}>
                      <button onClick={() => toggle(v)} className="mt-0.5 shrink-0" data-testid={`tx-toggle-${v.id}`}>
                        {v.completed
                          ? <CheckCircle2 className="h-5 w-5 text-[#2D6A4F]" />
                          : <Circle className="h-5 w-5 text-[#5C6B68]" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="font-bold text-[#0B1317]">{v.visit_type}</span>
                          <span className="inline-flex items-center gap-1 text-xs text-[#5C6B68]"><Calendar className="h-3 w-3" />{v.visit_date}</span>
                          {v.facility && <span className="inline-flex items-center gap-1 text-xs text-[#5C6B68]"><Building2 className="h-3 w-3" />{v.facility}</span>}
                          {!v.completed && <span className="rounded-full border border-[#FCD9A3] bg-[#FFF5E6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#B45309]">{t("tx.upcoming")}</span>}
                        </div>
                        {v.notes && <div className="mt-1 whitespace-pre-wrap text-sm text-[#0B1317]">{v.notes}</div>}
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-[#5C6B68]">
                          {t("docs.uploaded_by")} {v.created_by_role === "admin" ? t("tx.added_by_team") : (v.created_by_name || t("tx.added_by_you"))} · {new Date(v.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button onClick={() => remove(v)} className="inline-flex items-center rounded-md border border-[#F5B6B0] px-2 py-1 text-xs font-bold text-[#B91C1C] hover:bg-[#FDE8E6]" data-testid={`tx-delete-${v.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
